import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Check, MessageCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

export default function ShareCredentialsModal({ open, onClose, title = 'Account created', businessName, credentials, whatsappShareUrl }) {
  const [copied, setCopied] = useState(false);
  if (!open || !credentials) return null;

  const text = `Business: ${businessName || 'N/A'}\nLogin URL: ${credentials.loginUrl}\nEmail: ${credentials.email}\nTemp Password: ${credentials.tempPassword}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Credentials copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy');
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-zinc-950/50 backdrop-blur-sm grid place-items-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="share-credentials-modal"
      >
        <motion.div
          className="w-full max-w-lg bg-white rounded-lg shadow-xl overflow-hidden"
          initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8 }}
        >
          <div className="p-6 border-b border-zinc-200 flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-mono mb-1.5">✓ Success</div>
              <h2 className="font-display text-2xl font-bold text-zinc-950">{title}</h2>
              {businessName && <p className="text-sm text-zinc-500 mt-1">for <span className="font-mono text-zinc-900">{businessName}</span></p>}
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-md" data-testid="share-modal-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4 space-y-3">
              <Row label="Login URL" value={credentials.loginUrl} link />
              <Row label="Email" value={credentials.email} />
              <Row label="Temp Password" value={credentials.tempPassword} highlight />
            </div>

            <div className="text-xs text-zinc-500 leading-relaxed">
              Share these credentials with the user securely. They will be asked to change the password on first login.
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                onClick={copy}
                variant="outline"
                className="flex-1 h-11 border-zinc-200"
                data-testid="share-modal-copy-button"
              >
                {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy credentials</>}
              </Button>
              {whatsappShareUrl && (
                <a
                  href={whatsappShareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 h-11 rounded-md bg-[#25D366] hover:bg-[#128C7E] text-white font-medium inline-flex items-center justify-center gap-2 transition-colors"
                  data-testid="share-modal-whatsapp-button"
                >
                  <MessageCircle className="w-4 h-4" /> Share via WhatsApp
                </a>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Row({ label, value, highlight, link }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono min-w-[110px] pt-0.5">{label}</div>
      {link ? (
        <a href={value} target="_blank" rel="noreferrer" className="text-sm font-mono text-blue-600 hover:underline break-all text-right flex-1" data-testid={`share-modal-${label.toLowerCase().replace(/\s+/g,'-')}`}>{value}</a>
      ) : (
        <div className={`text-sm font-mono text-right flex-1 break-all ${highlight ? 'bg-yellow-50 border border-yellow-200 text-yellow-900 px-2 py-0.5 rounded font-bold' : 'text-zinc-950'}`} data-testid={`share-modal-${label.toLowerCase().replace(/\s+/g,'-')}`}>{value}</div>
      )}
    </div>
  );
}
